import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Loader2, AlertCircle, CheckCircle, XCircle, Clock, Copy, Check,
  ArrowLeftRight, FileCheck2, PlugZap, Send, Info, ChevronDown, ChevronRight, Webhook
} from 'lucide-react';
import { certificationApi } from '../../services/iso20022Api';
import { participantApiBase } from '../../utils/sandboxEndpoints';

/**
 * Modal with the full story of one certification case: what it proves, the
 * latest result, and exactly how to run it — request, headers, tenant-signed
 * sample body, expected response, and scenario walkthrough. Opened by clicking
 * a case in the Certification page's "Case detail" list.
 */

const EXCHANGE_BADGE = {
  SWITCH:    { icon: ArrowLeftRight, label: 'Certifying — switch exchange',            cls: 'bg-primary-50 text-primary-700 border-primary-200' },
  CALLBACK:  { icon: Webhook,        label: 'Certifying — callback (switch → you)',    cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  MESSAGE:   { icon: FileCheck2,     label: 'Prerequisite — local self-check',         cls: 'bg-gray-50 text-gray-600 border-gray-200' },
  TRANSPORT: { icon: PlugZap,        label: 'Infrastructure — pending deployment',     cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const STATUS_CHIP = {
  PASS:    { icon: CheckCircle, cls: 'bg-green-100 text-green-700' },
  FAIL:    { icon: XCircle,     cls: 'bg-red-100 text-red-700' },
  BLOCKED: { icon: Clock,       cls: 'bg-gray-100 text-gray-500' },
  SKIPPED: { icon: Clock,       cls: 'bg-gray-100 text-gray-500' },
  NOT_RUN: { icon: Clock,       cls: 'bg-gray-100 text-gray-500' },
};

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <button onClick={copy}
      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors ${
        copied ? 'bg-green-50 border-green-200 text-green-700'
               : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
      }`}>
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied!' : label}
    </button>
  );
}

/** One participant HTTP call: request block, sample body, expected result. */
function RequestBlock({ req, index, count }) {
  const [showBody, setShowBody] = useState(false);
  const headerLines = [
    `${req.method} ${participantApiBase('ISO20022')}${req.path}`,
    `Content-Type: ${req.contentType}`,
    'X-API-Key: <your key>',
    ...Object.entries(req.headers || {}).map(([k, v]) => `${k}: ${v}`),
  ];
  const curl = [
    `curl -X ${req.method} "${participantApiBase('ISO20022')}${req.path}"`,
    `-H "X-API-Key: $API_KEY"`,
    `-H "Content-Type: ${req.contentType}"`,
    ...Object.entries(req.headers || {}).map(([k, v]) => `-H "${k}: ${v}"`),
    ...(req.body ? [`--data-binary @sample.${req.contentType?.includes('xml') ? 'xml' : 'json'}`] : []),
  ].join(' \\\n  ');

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between bg-gray-50 px-3 py-2">
        <p className="text-xs font-medium text-gray-600">
          {count > 1 ? `Request ${index + 1} of ${count} — ` : ''}
          <span className="font-mono">{req.messageType}</span>
          {req.signed && <span className="ml-2 text-green-700">✓ signed with your certificate</span>}
        </p>
        <CopyButton text={curl} label="Copy as curl" />
      </div>
      <pre className="text-xs font-mono text-gray-800 bg-white px-3 py-2 overflow-x-auto">{headerLines.join('\n')}</pre>

      {req.body && (
        <div className="border-t border-gray-100">
          <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50/60">
            <button onClick={() => setShowBody(s => !s)}
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
              {showBody ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Sample body ({(req.body.length / 1024).toFixed(1)} KB{req.signed ? ', ready to send' : ''})
            </button>
            <CopyButton text={req.body} label="Copy body" />
          </div>
          {showBody && (
            <pre className="text-[11px] font-mono text-gray-700 bg-gray-50 px-3 py-2 overflow-x-auto max-h-56 overflow-y-auto">
              {req.body}
            </pre>
          )}
        </div>
      )}

      {req.expected && (
        <p className="text-xs text-gray-700 border-t border-gray-100 px-3 py-2">
          <span className="font-medium text-gray-500">Expected:</span> {req.expected}
        </p>
      )}
    </div>
  );
}

export default function CertCaseModal({ reportCase, onClose }) {
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);

  const caseId = reportCase?.caseId;

  useEffect(() => {
    if (!caseId) return;
    setData(null); setError(null);
    certificationApi.caseInstruction(caseId)
      .then(setData)
      .catch(e => setError(e.response?.status === 404
        ? `No instructions found for ${caseId}.`
        : (e.response?.data?.message || e.message)));
  }, [caseId]);

  // Close on Escape.
  const onKey = useCallback(e => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKey]);

  if (!reportCase) return null;

  const inst = data?.instruction;
  const status = reportCase.status || 'NOT_RUN';
  const chip = STATUS_CHIP[status] || STATUS_CHIP.NOT_RUN;
  const StatusIcon = chip.icon;
  const badge = inst ? (EXCHANGE_BADGE[inst.exchange] || EXCHANGE_BADGE.MESSAGE) : null;
  const observed = (reportCase.assertions || []).find(a => a.observed)?.observed;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         role="dialog" aria-modal="true" aria-label={`${caseId} details`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-900/50" onClick={onClose} />

      {/* Panel */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-gray-400">{caseId}</span>
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded ${chip.cls}`}>
                <StatusIcon className="w-3.5 h-3.5" /> {status.replace('_', ' ')}
              </span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                {(reportCase.obligation || inst?.obligation || '').toLowerCase()}
              </span>
            </div>
            <h2 className="text-base font-semibold text-gray-900 mt-1">{reportCase.title || inst?.title}</h2>
            {badge && (
              <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border mt-2 ${badge.cls}`}>
                <badge.icon className="w-3.5 h-3.5" /> {badge.label}
                {inst?.categoryName && <span className="text-gray-400">· {inst.categoryName}</span>}
              </span>
            )}
          </div>
          <button onClick={onClose} aria-label="Close"
            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto">
          {!data && !error && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Building instructions (signing samples with your certificate)…
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {inst && (
            <>
              {/* What it proves */}
              {inst.assertions?.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">What this case proves</h3>
                  <ul className="space-y-1">
                    {inst.assertions.map((a, i) => (
                      <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-primary-500 mt-0.5 shrink-0" /> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Latest observed result */}
              {observed && (
                <div className={`rounded-lg border p-3 text-sm ${
                  status === 'PASS' ? 'bg-green-50 border-green-200 text-green-800'
                    : status === 'FAIL' ? 'bg-red-50 border-red-200 text-red-800'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                }`}>
                  <span className="font-medium">Latest result:</span> {observed}
                </div>
              )}

              {/* How to run it */}
              {inst.requests?.length > 0 ? (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Send className="w-3.5 h-3.5" /> How to run it
                  </h3>
                  <div className="space-y-3">
                    {inst.requests.map((r, i) => (
                      <RequestBlock key={i} req={r} index={i} count={inst.requests.length} />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-800">
                    This case has no participant request to send yet
                    {inst.note ? ' — see the note below.' : '.'}
                  </p>
                </div>
              )}

              {/* Scenario walkthrough / notes */}
              {inst.note && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-blue-900">{inst.note}</p>
                </div>
              )}

              {/* Unsigned-samples warning */}
              {data && !data.tenantSigned && inst.requests?.some(r => r.contentType?.includes('xml')) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-800">{data.signingNote}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            The downloadable certification pack has every case as a Postman collection with the same samples.
          </p>
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 shrink-0">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
